/**
 * SIB Static Menu - Enhanced Navigation System
 * Mobile: Hamburger with slide-in drawer from right
 * Desktop: Hover dropdowns
 */

class SibStaticMenu {
  constructor() {
    this.menu = document.querySelector('.sib-static-menu');
    if (!this.menu) return;

    this.drawer = this.menu.querySelector('.sib-static-menu__drawer');
    this.overlay = this.menu.querySelector('.sib-static-menu__overlay');
    this.closeBtn = this.menu.querySelector('.sib-static-menu__close');
    
    // External trigger from top navbar
    this.externalHamburger = document.querySelector('[data-sib-menu-icon]');
    
    this.isMobile = window.innerWidth < 960;
    this.init();
  }

  init() {
    // Add has-submenu class to all items with submenus (for both mobile and desktop)
    this.addSubmenuIndicators();

    // Listen to external hamburger button from top navbar
    if (this.externalHamburger) {
      this.externalHamburger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openDrawer();
      });
    }

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.closeDrawer());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.closeDrawer());
    }

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.menu.classList.contains('is-open')) {
        this.closeDrawer();
      }
    });

    // Handle nested menus in drawer
    this.initDrawerToggles();

    // Handle responsive behavior
    this.handleResponsive();
    window.addEventListener('resize', this.debounce(() => this.handleResponsive(), 150));
  }

  addSubmenuIndicators() {
    // Add .has-submenu class to all items that have child UL elements
    const allMenuItems = this.menu.querySelectorAll(
      '.sib-static-menu__menu-item, ' +
      '.sib-static-menu__submenu-item, ' +
      '.sib-static-menu__sub-submenu-item'
    );

    allMenuItems.forEach(item => {
      const submenu = item.querySelector(':scope > ul');
      if (submenu) {
        item.classList.add('has-submenu');
      }
    });
  }

  openDrawer() {
    this.menu.classList.add('is-open');
    document.body.classList.add('sib-menu-open');
    this.hamburger.setAttribute('aria-expanded', 'true');
    
    // Focus trap
    setTimeout(() => {
      this.closeBtn?.focus();
    }, 300);
  }

  closeDrawer() {
    this.menu.classList.remove('is-open');
    document.body.classList.remove('sib-menu-open');
    this.hamburger.setAttribute('aria-expanded', 'false');
    
    // Return focus to hamburger
    this.hamburger?.focus();
    
    // Close all open submenus
    this.closeAllSubmenus();
  }

  initDrawerToggles() {
    const drawerMenus = this.drawer.querySelectorAll('.sib-static-menu__menus')[0];
    if (!drawerMenus) return;

    const itemsWithSubmenus = drawerMenus.querySelectorAll('.has-submenu');

    itemsWithSubmenus.forEach(item => {
      const link = item.querySelector(':scope > a');
      const submenu = item.querySelector(':scope > ul');

      if (link && submenu) {
        link.addEventListener('click', (e) => {
          const isOpen = item.classList.contains('is-open');
          
          // If submenu is NOT open, prevent navigation and open it
          if (!isOpen) {
            e.preventDefault();
            this.toggleSubmenu(item, submenu);
          }
          // If submenu IS already open, allow navigation to the link URL
        });
      }
    });
  }

  toggleSubmenu(item, submenu) {
    const isOpen = item.classList.contains('is-open');

    if (isOpen) {
      this.closeSubmenu(item, submenu);
    } else {
      // Close siblings at the same level (Accordion behavior)
      this.closeSiblings(item);
      this.openSubmenu(item, submenu);
    }
  }

  closeSiblings(currentItem) {
    // Find all siblings at the same level
    const parent = currentItem.parentElement;
    const siblings = parent.querySelectorAll(':scope > .has-submenu');
    
    siblings.forEach(sibling => {
      if (sibling !== currentItem && sibling.classList.contains('is-open')) {
        const siblingSubmenu = sibling.querySelector(':scope > ul');
        this.closeSubmenu(sibling, siblingSubmenu);
      }
    });
  }

  openSubmenu(item, submenu) {
    item.classList.add('is-open');
    submenu.classList.add('is-open');
  }

  closeSubmenu(item, submenu) {
    item.classList.remove('is-open');
    submenu.classList.remove('is-open');

    // Close all child submenus
    const childItems = submenu.querySelectorAll('.is-open');
    childItems.forEach(child => {
      child.classList.remove('is-open');
    });
  }

  closeAllSubmenus() {
    const openItems = this.drawer.querySelectorAll('.is-open');
    openItems.forEach(item => {
      item.classList.remove('is-open');
    });
  }

  handleResponsive() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 960;

    // Close drawer if switching to desktop
    if (wasMobile && !this.isMobile) {
      this.closeDrawer();
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SibStaticMenu();
  });
} else {
  new SibStaticMenu();
}
